import React from "react"
import {Address, toPhoneNumber} from "../../resources/common"
import {Vendor} from "../../resources/bills"
import {TagsViewerKeyValue} from "../Tags/TagsViewer"
import {
	KeyValueCard,
	KeyValueCardContainer,
	KeyValueCardKey,
	KeyValueCardSubHeader,
	KeyValueCardValue,
} from "../KeyValueCard/KeyValueCard"

function formatAddress(address?: Address): string | undefined {
	if (!address) {
		return undefined
	}

	const formatted = [address.street, address.street2, address.city, address.state, address.postalCode, address.country]
		.filter(Boolean)
		.join(", ")

	return formatted || undefined
}

function maskSensitiveNumber(value?: string): string | undefined {
	if (!value) {
		return undefined
	}

	return `••••${value.slice(-4)}`
}

function DetailRow({label, children}: {label: string; children?: React.ReactNode}) {
	if (!children) {
		return null
	}

	return (
		<>
			<KeyValueCardKey>{label}</KeyValueCardKey>
			<KeyValueCardValue>{children}</KeyValueCardValue>
		</>
	)
}

export function VendorDetails({vendor}: {vendor: Vendor}) {
	const attributes = vendor.attributes ?? {}
	const phoneNumber = attributes.phone?.number ? toPhoneNumber(attributes.phone) : undefined

	return (
		<KeyValueCard title="Vendor Details" className="vendor-info-card">
			<KeyValueCardContainer>
				<DetailRow label="Legal Name">{attributes.legalName}</DetailRow>
				<DetailRow label="Email">{attributes.email}</DetailRow>
				<DetailRow label="Phone">{phoneNumber}</DetailRow>
				<DetailRow label="Address">{formatAddress(attributes.address)}</DetailRow>
				{attributes.website && (
					<DetailRow label="Website">
						<a className="link" href={attributes.website} target="_blank" rel="noreferrer">
							{attributes.website}
						</a>
					</DetailRow>
				)}
				{attributes.tags && Object.keys(attributes.tags).length > 0 && <TagsViewerKeyValue tags={attributes.tags} />}
			</KeyValueCardContainer>
		</KeyValueCard>
	)
}

export function VendorPaymentMethods({vendor}: {vendor: Vendor}) {
	const ach = vendor.attributes?.paymentMethods?.ach
	const check = vendor.attributes?.paymentMethods?.check
	const checkAddress = formatAddress(check?.address)

	return (
		<KeyValueCard title="Payment Methods" className="vendor-info-card">
			{!ach && !check && (
				<KeyValueCardContainer>
					<DetailRow label="Methods">—</DetailRow>
				</KeyValueCardContainer>
			)}
			{ach && (
				<>
					<KeyValueCardSubHeader title="ACH" />
					<KeyValueCardContainer>
						<DetailRow label="Name">{ach.name}</DetailRow>
						<DetailRow label="Routing Number">{maskSensitiveNumber(ach.routingNumber)}</DetailRow>
						<DetailRow label="Account Number">{maskSensitiveNumber(ach.accountNumber)}</DetailRow>
					</KeyValueCardContainer>
				</>
			)}
			{check && (
				<>
					<KeyValueCardSubHeader title="Check" />
					<KeyValueCardContainer>
						<DetailRow label="Name">{check.name}</DetailRow>
						<DetailRow label="Address">{checkAddress}</DetailRow>
					</KeyValueCardContainer>
				</>
			)}
		</KeyValueCard>
	)
}
